import React from "react";
import {
  Clock,
  MapPin,
  Zap,
  TrendingUp,
  Pause,
  Activity,
  Timer,
  Route,
} from "lucide-react";
import type { ProcessedRoute } from "../../types/route";
import {
  formatDuration,
  formatDistance,
  formatSpeed,
} from "../../utils/formatters";

interface RouteAnalyticsProps {
  route: ProcessedRoute;
}

export const RouteAnalytics: React.FC<RouteAnalyticsProps> = ({ route }) => {
  // Calculate additional metric
  const totalStopTime = route.stops.reduce(
    (total, stop) => total + stop.duration_minutes,
    0,
  );
  const movingTime = route.moving_time_minutes || 0;
  const hoursOnRoad = route.hours_on_road || 0;
  const efficiencyRatio =
    route.duration_minutes > 0
      ? (movingTime / route.duration_minutes) * 100
      : 0;

  const primaryStats = [
    {
      icon: MapPin,
      label: "Total Distance",
      value: formatDistance(route.total_distance_km),
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      icon: Clock,
      label: "Total Duration",
      value: formatDuration(route.duration_minutes),
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      icon: Timer,
      label: "Hours on Road",
      value: `${hoursOnRoad.toFixed(1)}h`,
      subtitle: `${formatDuration(movingTime)} moving`,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      icon: Activity,
      label: "GPS Points",
      value: route.total_points.toLocaleString(),
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
    },
  ];

  const speedStats = [
    {
      icon: Zap,
      label: "Avg Speed (Moving)",
      value: formatSpeed(route.moving_average_speed_kmh || 0),
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
    },
    {
      icon: Route,
      label: "Avg Speed (Overall)",
      value: formatSpeed(route.average_speed_kmh || 0),
      subtitle: "Includes stops",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      icon: TrendingUp,
      label: "Max Speed",
      value: formatSpeed(route.max_speed_kmh),
      color: "text-red-600",
      bgColor: "bg-red-50",
    },
    {
      icon: Pause,
      label: "Stops",
      value: route.stops.length.toString(),
      subtitle: formatDuration(totalStopTime),
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
  ];

  const segmentStats = {
    moving: route.segments.filter((s) => s.type === "moving"),
    stopped: route.segments.filter((s) => s.type === "stopped"),
    slow: route.segments.filter((s) => s.type === "slow"),
  };

  const routeHeader = (
    <p className="text-sm text-gray-600 mb-4">
      {route.total_trips} movement segment{route.total_trips !== 1 ? "s" : ""} ·{" "}
      {formatDistance(route.total_distance_km)} active
      {route.trip_gaps.length > 0 &&
        ` · ${route.trip_gaps.length} gap${route.trip_gaps.length !== 1 ? "s" : ""}`}
    </p>
  );

  return (
    <div className="space-y-6">
      {/* Primary Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-blue-600" />
          Trip Overview
        </h3>
        {routeHeader}

        <div className="grid grid-cols-2 gap-4">
          {primaryStats.map((stat, index) => (
            <div key={index} className={`p-3 rounded-lg ${stat.bgColor}`}>
              <div className="flex items-center justify-between">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                <div className="text-right">
                  <div className={`text-lg font-bold ${stat.color}`}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                  {stat.subtitle && (
                    <div className="text-xs text-gray-500 mt-1">
                      {stat.subtitle}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Speed and Performance Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
          <Zap className="w-5 h-5 mr-2 text-yellow-600" />
          Speed & Performance
        </h3>

        <div className="grid grid-cols-2 gap-4">
          {speedStats.map((stat, index) => (
            <div key={index} className={`p-3 rounded-lg ${stat.bgColor}`}>
              <div className="flex items-center justify-between">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                <div className="text-right">
                  <div className={`text-lg font-bold ${stat.color}`}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                  {stat.subtitle && (
                    <div className="text-xs text-gray-500 mt-1">
                      {stat.subtitle}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Efficiency Analysis */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-medium text-gray-900 mb-4">Movement Efficiency</h4>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Moving Efficiency</span>
            <span className="font-medium">{efficiencyRatio.toFixed(1)}%</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-gray-600">Active Riding Time</span>
            <span className="font-medium text-green-600">
              {formatDuration(movingTime)}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-gray-600">Stop Time</span>
            <span className="font-medium text-red-600">
              {formatDuration(totalStopTime)}
            </span>
          </div>
        </div>

        {/* Time distribution visualization */}
        <div className="mt-4">
          <div className="flex h-4 rounded overflow-hidden border border-gray-200">
            <div
              className="bg-green-400"
              style={{ width: `${efficiencyRatio}%` }}
              title={`Moving: ${formatDuration(movingTime)}`}
            />
            <div
              className="bg-red-400"
              style={{ width: `${100 - efficiencyRatio}%` }}
              title={`Stopped: ${formatDuration(totalStopTime)}`}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>Moving ({efficiencyRatio.toFixed(1)}%)</span>
            <span>Stopped ({(100 - efficiencyRatio).toFixed(1)}%)</span>
          </div>
        </div>
      </div>

      {/* Movement Segments Analysis */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-medium text-gray-900 mb-4">Movement Segments</h4>

        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {segmentStats.moving.length}
            </div>
            <div className="text-sm text-gray-600">Moving</div>
            <div className="text-xs text-gray-500 mt-1">
              Avg:{" "}
              {segmentStats.moving.length > 0
                ? (
                    segmentStats.moving.reduce(
                      (sum, s) => sum + s.average_speed_kmh,
                      0,
                    ) / segmentStats.moving.length
                  ).toFixed(1)
                : "0"}{" "}
              km/h
            </div>
          </div>

          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {segmentStats.slow.length}
            </div>
            <div className="text-sm text-gray-600">Slow</div>
            <div className="text-xs text-gray-500 mt-1">{"<5 km/h"}</div>
          </div>

          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {segmentStats.stopped.length}
            </div>
            <div className="text-sm text-gray-600">Stopped</div>
            <div className="text-xs text-gray-500 mt-1">0 km/h</div>
          </div>
        </div>
      </div>

      {/* Stop Details */}
      {route.stops.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            <Pause className="w-4 h-4 mr-2 text-purple-600" />
            Stop Details ({route.stops.length})
          </h4>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {route.stops.map((stop, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium">Stop {index + 1}</div>
                  <div className="text-sm text-gray-600">
                    {new Date(stop.start_time).toLocaleTimeString()} -{" "}
                    {new Date(stop.end_time).toLocaleTimeString()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {stop.location.latitude.toFixed(4)},{" "}
                    {stop.location.longitude.toFixed(4)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">
                    {formatDuration(stop.duration_minutes)}
                  </div>
                  <div className="text-sm text-gray-600 capitalize">
                    {stop.reason}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Card */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6 border border-blue-200">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
          <Activity className="w-4 h-4 mr-2 text-blue-600" />
          Trip Summary
        </h4>

        <div className="text-sm text-gray-700 space-y-1">
          <p>
            <span className="font-medium">Bike {route.bike_id}</span> traveled{" "}
            <span className="font-medium text-blue-600">
              {formatDistance(route.total_distance_km)}
            </span>{" "}
            over{" "}
            <span className="font-medium">
              {formatDuration(route.duration_minutes)}
            </span>
          </p>
          <p>
            Active riding time was{" "}
            <span className="font-medium text-green-600">
              {hoursOnRoad.toFixed(1)} hours
            </span>{" "}
            with an average speed of{" "}
            <span className="font-medium text-yellow-600">
              {formatSpeed(route.moving_average_speed_kmh || 0)}
            </span>
          </p>
          <p>
            Recorded{" "}
            <span className="font-medium">
              {route.total_points.toLocaleString()}
            </span>{" "}
            GPS points across {route.total_trips} segment
            {route.total_trips !== 1 ? "s" : ""}
            {route.stops.length > 0 && (
              <span>
                {" "}
                with {route.stops.length} stop
                {route.stops.length !== 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
